// box z produktami (Wise UI) - karuzela produktów na stronie głównej

import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Slide, Slider } from 'pure-react-carousel';
import classnames from 'classnames';

import { useGetHomeProductsBox } from 'api';
import { Link } from 'components/controls';
import { useRWD } from 'hooks';
import WiseCarousel from 'pages/wise/WiseProduct/components/WiseCarousel';
import { WiseProductItem } from 'components/containers';

import styles from 'theme/components/wise/WiseProductsBox/WiseProductsBox.module.scss';

interface IProps {
  productBoxId: string;
}

const WISE_PRODUCT_CARD_WIDTH_PX = 260;
const CAROUSEL_MAX_VISIBLE_SLIDES = 5;
const SLIDE_GAP_PX = 8;
const WISE_PRODUCT_CARD_HEIGHT_PX = 465;

function countCardsThatFitRow(widthPx: number): number {
  if (widthPx <= 0) {
    return 1;
  }
  const slot = WISE_PRODUCT_CARD_WIDTH_PX + SLIDE_GAP_PX;
  return Math.max(1, Math.floor((widthPx + SLIDE_GAP_PX) / slot));
}

const WiseProductsBox: FC<IProps> = ({ productBoxId }) => {
  const [fitsInRow, setFitsInRow] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useRWD();

  const { data: homeProductsBoxData, isLoading, isFetching } = useGetHomeProductsBox(productBoxId);

  const products = homeProductsBoxData?.products ?? [];
  const itemCount = products.length;
  const loadingEmpty = (isLoading || isFetching) && itemCount === 0;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const measure = () => {
      setFitsInRow(countCardsThatFitRow(el.getBoundingClientRect().width));
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [itemCount]);

  const visibleSlidesInCarousel = useMemo(() => {
    if (itemCount < 1) {
      return 1;
    }
    if (isMobile) {
      // Na mobile pokazujemy 1 pełen kafelek + zajawkę kolejnego (1.3) - zgodnie z mockupem Figma.
      return Math.min(1.3, itemCount);
    }
    return Math.min(
      CAROUSEL_MAX_VISIBLE_SLIDES,
      itemCount,
      Math.max(1, fitsInRow ?? CAROUSEL_MAX_VISIBLE_SLIDES)
    );
  }, [itemCount, isMobile, fitsInRow]);

  const skeletonSlideCount = useMemo(() => {
    if (isMobile) return 1;
    return Math.min(CAROUSEL_MAX_VISIBLE_SLIDES, Math.max(1, fitsInRow ?? CAROUSEL_MAX_VISIBLE_SLIDES));
  }, [isMobile, fitsInRow]);

  // Strzałki nawigacji ukryte na mobile - w nagłówku robiłyby ciasno z tytułem i CTA.
  // Mobile użytkownik nawiguje swipem; na desktop strzałki widoczne w nagłówku obok CTA.
  const showNavArrows = !isMobile;

  const path = homeProductsBoxData?.url
    ? new URL(homeProductsBoxData.url).pathname
    : '/';

  const headerAction =
    path  ? (
      <Link className={styles.viewAllCta} to={path}>
        <Trans>Zobacz wszystkie</Trans>
      </Link>
    ) : undefined;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Home-components-ProductsBox-WiseProductsBox'
      )}>
      <div ref={contentRef} className={styles.content}>
        {(itemCount > 0 || loadingEmpty) && (
          <WiseCarousel
            className={styles.carousel}
            totalSlides={itemCount}
            visibleSlides={itemCount > 0 ? visibleSlidesInCarousel : skeletonSlideCount}
            naturalSlideWidth={WISE_PRODUCT_CARD_WIDTH_PX}
            naturalSlideHeight={WISE_PRODUCT_CARD_HEIGHT_PX}
            relaxHeight
            title={homeProductsBoxData?.name}
            headerAction={itemCount > 0 ? headerAction : undefined}
            headerLayout="stacked-mobile"
            showNavArrows={itemCount > 0 ? showNavArrows : false}
            disableKeyboard
            disableAnimation={loadingEmpty || isFetching}
            isLoading={loadingEmpty}>
            {itemCount > 0 ? (
              <Slider>
                {products.map((product, index) => (
                  <Slide key={product.id} index={index} className={styles.carouselSlide}>
                    <div className={styles.productWrapper}>
                      <WiseProductItem product={product} />
                    </div>
                  </Slide>
                ))}
              </Slider>
            ) : null}
          </WiseCarousel>
        )}
      </div>
    </div>
  );
};

export default WiseProductsBox;
